import { Component, NgZone, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { filter } from 'lodash';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, forkJoin } from 'rxjs';
import { AssetClassLandDeleted, AssetClassLandDeletedFromTrash, AssetClassLandDuplicate, AssetClassLandPageRequested } from 'src/app/core/comparable/_actions/asset-class-land.actions';
import { AssetClassLandDataSource } from 'src/app/core/comparable/_data-sources/asset-class-land.datasource';
import { AssetClassLandModel } from 'src/app/core/comparable/_models/asset-class-land.model';
import { selectAdminTrashedAssetClassLandCount, selectTrashedAssetClassLandCount } from 'src/app/core/comparable/_selectors/asset-class-land.selectors';
import { AssetClassLandService } from 'src/app/core/comparable/_services/asset-class-land.service';
import { User, currentUser, hasPermission } from 'src/app/core/mad-auth/mad-auth.store';
import { AppState } from 'src/app/core/reducers';
import { LayoutUtilsService, QueryParamsModel } from 'src/app/core/_base/crud';
import { fetchMapMarkers } from 'src/app/core/_base/crud/utils/map-marker.util';
import { ComparableMarkerLayer, MarkerLayerPin } from 'src/app/core/map/comparable-marker-layer';
import { ComparableMapPin, ComparableMapService } from 'src/app/core/comparable/_services/comparable-map.service';
import { emptyFilter, FilterModel, FilterChange } from '../../../shared_components/filter-section/filter-section.component';
import { MapMarkerInfoWindowComponent, MapMarkerInfoWindowDialogInput } from '../../../shared_components/map-marker-info-window/map-marker-info-window.component';
import { ToMarkerItemConverterService } from '../../../shared_components/map-marker-info-window/to-marker-item-converter-service.service';
import { environment } from 'src/environments/environment';
import { MapsService } from 'src/app/core/map/maps.service';
import { debounceTime, map } from 'rxjs/operators';
import {AgmMap, MapsAPILoader} from '@agm/core';
import { zoomForViewport } from 'src/app/core/v2/map/viewport-to-zoom';

@Component({
  selector: 'kt-lands-map',
  templateUrl: './lands-map.component.html',
  styleUrls: ['./lands-map.component.scss']
})
export class LandsMapComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild('sort1', {static: true}) sort: MatSort;

  @ViewChild('search', {static: false})
  public searchElementRef: ElementRef;
  private geoCoder = new google.maps.Geocoder();

  loading$: Observable<boolean>;
  dataSource: AssetClassLandDataSource;
  assetClasses: AssetClassLandModel[] = [];
  previous;
  icon = {
      url: './assets/media/icons/my_marker.svg'
  };

  isFirst = true;
  private _centerLat$ = new BehaviorSubject<number>(null)
  centerLat$ = combineLatest([
    this._centerLat$,
    this.mapsService.latitude$
  ]).pipe(map(([initial, def]) => {
    return initial == null ? def : initial
  }))
  private _centerLng$ = new BehaviorSubject<number>(null)
  centerLng$ = combineLatest([
    this._centerLng$,
    this.mapsService.longitude$
  ]).pipe(map(([initial, def]) => {
    return initial == null ? def : initial
  }))
  zoom = 15;
  map: any;
  currentCenter: { lat: 0, lng: 0 };
  mapAction = new BehaviorSubject<boolean>(true);

  lastQueryParams: QueryParamsModel;
  filterModel: FilterModel = emptyFilter();
  filter$: BehaviorSubject<FilterModel> = new BehaviorSubject(this.filterModel);
  filterObs$ = this.filter$.asObservable();
  displayedColumns = ['id', 'sub_type', 'size', 'consideration', 'source_date', 'actions'];

  /**
   * One entry per distinct location, kept here rather than rendered through the template:
   * `ComparableMarkerLayer` draws them straight onto the map. Held so a layer created after
   * the first response still gets what has already been fetched.
   */
  private _pins: MarkerLayerPin<ComparableMapPin>[] = []
  private _markerLayer: ComparableMarkerLayer<ComparableMapPin> = null
  /**
   * Drives the map's own request. The table's request is paginated; the map's is not, so
   * the two cannot share one -- with 25 rows a page the map used to draw 25 markers and
   * silently drop every other comparable in the viewport.
   */
  private _mapFilter$ = new Subject<FilterModel>();
  /**
   * Drives the table's page request. AGM emits boundsChange continuously while the map is
   * panned or zoomed, and each one used to dispatch a page request of its own -- a burst of
   * them per drag, against the list endpoint that reads every row's relations.
   */
  private _listRequest$ = new Subject<void>();
  subscriptions: Array<Subscription> = [];
    trashCnt = 0;
    adminTrashCnt = 0;


  canAccessAdminTrash$ = this.store.pipe(select(hasPermission(['admin_trash'])))
  currentUser: User|null

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toMarkerItemService: ToMarkerItemConverterService,
    private dialog: MatDialog,
        private layoutService: LayoutUtilsService,
        private service: AssetClassLandService,
    private comparableMapService: ComparableMapService,
    private mapsService: MapsService,
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
  ) { }

  ngOnInit(): void {
    const userSub = this.store.select(currentUser).subscribe(user => this.currentUser = user)
    this.subscriptions.push(userSub)
    this.loading$ = this.mapAction.asObservable();

    this.lastQueryParams = new QueryParamsModel({});
    // const lastyQueryParamsSubscriptions = this.store.pipe(
    //     select(selectAssetClassLandsLastQuery),
    //     take(1)
    // ).subscribe(res => {
    //     if (res.isEmpty()) {
    //         return;
    //     }
    //     this.lastQueryParams = res;
    //     this.updateFilter(res.filter);
    // });
    // this.subscriptions.push(lastyQueryParamsSubscriptions);

    this.dataSource = new AssetClassLandDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject.subscribe(res => {
      this.assetClasses = res;
      this.mapAction.next(false);
    });
    this.subscriptions.push(entitiesSubscription);

    const listSubscription = this._listRequest$.pipe(

        debounceTime(300)

    ).subscribe(() => this._dispatchList());

    this.subscriptions.push(listSubscription);


    const markersSubscription = fetchMapMarkers<ComparableMapPin>(
      this._mapFilter$.asObservable(),
      filter => this.comparableMapService.load(13, filter)
    ).subscribe(res => this._setMarkers(res));
    this.subscriptions.push(markersSubscription);

        const trashSubscription = this.store.pipe(
            select(selectTrashedAssetClassLandCount)
        ).subscribe(res => {
            this.trashCnt = res;
        });
        this.subscriptions.push(trashSubscription);

        const adminTrashSubscription = this.store.pipe(
            select(selectAdminTrashedAssetClassLandCount)
        ).subscribe(res => {
            this.adminTrashCnt = res;
        });
        this.subscriptions.push(adminTrashSubscription);
  }
  onFilterChange(event: FilterChange) {
      if (event.page != null) {
          this.paginator.pageIndex = event.page
      }
      // The filter section keeps its own copy of the model and never learns about the
      // map's viewport, so its filter always comes back with bounds cleared. Keeping the
      // bounds the map last reported is what stops a filter change fetching the world.
      this.filterModel = Object.assign({}, event.filter, {
          bounds: event.filter.bounds ? event.filter.bounds : this.filterModel.bounds
      }) as FilterModel;
      this.loadList();
      this._loadMapMarkers();
  }

  ngOnDestroy(): void {
    if (this._markerLayer) {
      this._markerLayer.destroy()
    }
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  mapReady(event: any) {
    this.map = event;
    this.map.controls[google.maps.ControlPosition.RIGHT_CENTER].push(document.getElementById('Profile'));

    this._markerLayer = new ComparableMarkerLayer<ComparableMapPin>(
        this.map,
        this.ngZone,
        {
            iconUrl: this.icon.url,
            labelClassName: (_pin, isStack) => isStack ? 'bg-op-stack' : 'bg-op'
        },
        pins => this.showInfo(pins)
    )
    this._markerLayer.setPins(this._pins)

    // this._initializeDrawingManager(this.map)
    this.map.addListener('dragend', () => {
      this._centerLat$.next(this.currentCenter.lat);
      this._centerLng$.next(this.currentCenter.lng);

      this.mapAction.next(true);
      this.paginator.pageIndex = 0;
      this.loadList();
    })

    this.mapsAPILoader.load().then(() => {
        this.geoCoder = new google.maps.Geocoder();
        if (this.searchElementRef) {
        const autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, {
            types: ['geocode']
        });
        autocomplete.addListener('place_changed', () => {
            this.ngZone.run(() => {
            // get the place result
            const place: google.maps.places.PlaceResult = autocomplete.getPlace();
            // verify result
            if (place.geometry === undefined || place.geometry === null) {
                return;
            }

            // set latitude, longitude and zoom
            this._centerLat$.next(place.geometry.location.lat())
            this._centerLng$.next(place.geometry.location.lng())
            this.zoom = zoomForViewport(place.geometry.viewport);
            });
        });
        }
    });

    // this.mapClickListener = this.map.addListener('click', (e: google.maps.MouseEvent) => {
    //     this.ngZone.run(() => {
    //         this._mapRightClick(e);
    //     })
    // })
  }

  // private _mapRightClick($event: google.maps.MouseEvent) {
  //     const lat = $event.latLng.lat();
  //     const lng = $event.latLng.lng();
  //     const refs = []
  //     this._shapes.forEach((value, _ref) => {
  //       if (google.maps.geometry.poly.containsLocation(new google.maps.LatLng(lat, lng), value.shape)) {
  //         refs.push(_ref);
  //       }
  //     });
  //     const infoData = this.assetClasses.filter(ac => refs.includes(ac.ref_num));
  //     if (infoData.length == 0) {
  //       return;
  //     }
  //     const data = this.toMarkerItemService.convert('comparable', infoData.map(id => ({...id, ac_type: 13})));
  //     this.dialog.open<MapMarkerInfoWindowComponent, MapMarkerInfoWindowDialogInput>(MapMarkerInfoWindowComponent, {
  //         data: {
  //             modal_title: data.modal_title,
  //             entities: data.entities
  //         }
  //     });
  // }

  // private _initializeDrawingManager(map: google.maps.Map) {
  //   const options: google.maps.drawing.DrawingManagerOptions = {
  //     drawingMode: null,
  //     drawingControl: false,
  //     drawingControlOptions: {
  //       position: google.maps.ControlPosition.TOP_CENTER,
  //       drawingModes: [
  //         google.maps.drawing.OverlayType.POLYGON
  //       ]
  //     },
  //     polygonOptions: {
  //       draggable: false,
  //       editable: true
  //     }
  //   }

  //   this._drawingManager = new google.maps.drawing.DrawingManager(options);
  //   this._drawingManager.setMap(map);

  //   google.maps.event.addListener(this._drawingManager, 'overlaycomplete', (event) => {
  //     // if (event.type === google.maps.drawing.OverlayType.POLYGON) {
  //     //   const points = this._getPointsFromPath(event.overlay.getPath());
  //     //   this._createPolygon(points);
  //     //   event.overlay.setMap(null);
  //     // }
  //     // if (event.type !== google.maps.drawing.OverlayType.MARKER) {
  //     //   this._disableDrawing();
  //     // }
  //   })
  // }

  boundsChanged(_latLngBound) {
      const _new = Object.assign({}, this.filterModel) as FilterModel;
      if (_new.bounds == null) {
          _new.bounds = {ne_lat: 0, sw_lat: 0, ne_lng: 0, sw_lng: 0}
      } else {
          _new.bounds = Object.assign({}, _new.bounds);
      }
      _new.bounds.sw_lat = _latLngBound.getSouthWest().lat();
      _new.bounds.ne_lat = _latLngBound.getNorthEast().lat();
      _new.bounds.sw_lng = _latLngBound.getSouthWest().lng();
      _new.bounds.ne_lng = _latLngBound.getNorthEast().lng();

      this.filterModel = _new;
    if (this.isFirst) {
      this.previous = null;
      this.mapAction.next(true);
      this.isFirst = !this.isFirst;
      this.paginator.pageIndex = 0;
    }
      this.loadList();
      this._loadMapMarkers();
  }

  zoomChanged(event) {
    this.isFirst = true;
  }

  centerChange(event: any) {
    if (event) {
      this.currentCenter = {lat: event.lat, lng: event.lng};
    }
  }

  setLocation() {
    this.setCurrentLocation();
  }

  private setCurrentLocation() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        if (position.coords.latitude && position.coords.longitude) {
          this._centerLat$.next(position.coords.latitude);
          this._centerLng$.next(position.coords.longitude);
          this.zoom = 15;
        }
      })
    } else {
      alert('Position not available');
    }
  }
    getItemCssClassByStatus(status: number): string {
        switch (status) {
            case 0:
                return 'warning';
            case 2:
                return 'info';
            default:
               return 'success';
        }
    }
    getItemStatusString(status: number): string {
        switch (status) {
            case 0:
                return 'Draft';
            case 2:
                return 'imported';
            default:
                return 'Validated';
        }
    }

  /**
   * Rebuilt from scratch on every response. Lands sharing a location become one pin, the way
   * the other comparable maps already group theirs -- keyed by refNum, as this was, two lands
   * at one address drew two markers on the same spot and the back one could not be clicked.
   */
  private _setMarkers(pins: ComparableMapPin[]) {
    const grouped = new Map<string, MarkerLayerPin<ComparableMapPin>>()
    pins.forEach(pin => {
      const center = this._pinLocation(pin)
      if (center == null) {
        // No coordinates and no parcel outline -- nowhere to put it.
        return
      }
      const latitude = center.lat()
      const longitude = center.lng()
      const key = latitude + '_' + longitude
      const existing = grouped.get(key)
      if (existing) {
        existing.title = existing.title + '/' + pin.refNum
        existing.items.push(pin)
      } else {
        grouped.set(key, {
          key,
          title: pin.refNum,
          latitude,
          longitude,
          items: [pin]
        })
      }
    })

    this._pins = Array.from(grouped.values())
    if (this._markerLayer) {
      this._markerLayer.setPins(this._pins)
    }
  }

  /** A land's own coordinates, or failing that the centre of the parcel it outlines. */
  private _pinLocation(pin: ComparableMapPin): google.maps.LatLng | null {
    if (pin.latitude != null && pin.longitude != null) {
      return new google.maps.LatLng(pin.latitude, pin.longitude);
    }
    const points = pin.landPaths.filter(p => p.lat != null && p.lng != null);
    if (points.length == 0) {
      return null;
    }
    const bounds = new google.maps.LatLngBounds();
    points.forEach(p => bounds.extend({lat: p.lat, lng: p.lng}));
    return bounds.getCenter();
  }

  private _loadMapMarkers() {
    this._mapFilter$.next(Object.assign({}, this.filterModel) as FilterModel);
  }

  loadList() {
      this._listRequest$.next();
  }

  private _dispatchList() {
    let queryParams = null;
    if (this.lastQueryParams.isEmpty()) {
      queryParams = new QueryParamsModel(
        this.filterModel,
        this.sort.direction,
        this.sort.active,
        this.paginator.pageIndex + 1,
        this.paginator.pageSize
      )
    } else {
      queryParams = this.lastQueryParams;
    }
    this.lastQueryParams = new QueryParamsModel({});
    this.store.dispatch(new AssetClassLandPageRequested({page: queryParams}))
  }

    addComparable() {
        this.editComparable(null);
    }
    editComparable(ac: AssetClassLandModel) {
        if (ac) {
            this.router.navigate(['../edit', ac.id], {relativeTo: this.activatedRoute});
        } else {
            this.router.navigate(['../add'], {relativeTo: this.activatedRoute})
        }
    }
    deleteComparable(ac: AssetClassLandModel) {
        this.store.dispatch(new AssetClassLandDeleted({id: ac.id}));
    }

    // loadList() {
    //     let queryParams = null;
    //     if (this.lastQueryParams.isEmpty()) {
    //         queryParams = new QueryParamsModel(
    //             this.filterModel,
    //             this.sort.direction,
    //             this.sort.active,
    //             this.paginator.pageIndex + 1,
    //             this.paginator.pageSize
    //         );
    //     } else {
    //         queryParams = this.lastQueryParams;
    //     }
    //     // Call request from server
    //     this.lastQueryParams = new QueryParamsModel({});
    //     this.store.dispatch(new AssetClassMapPageRequested({page: queryParams}));
    // }
    getLabel(title) {
        return {
            color: '#000000',
            fontFamily: '',
            fontSize: '14px',
            fontWeight: 'bold',
            className: 'bg-op',
            text: title,
        };
    }

    /**
     * The markers carry only what a pin needs, so the records behind the one clicked are
     * fetched here -- a handful of comparables at one coordinate, not a viewport of them.
     */
    showInfo(pins: ComparableMapPin[]) {
        forkJoin(pins.map(pin => this.service.getAssetClass(pin.id))).subscribe(responses => {
            const data = this.toMarkerItemService.convert('comparable', responses.map(res => ({...res.data, ac_type: 13})));
            this.dialog.open<MapMarkerInfoWindowComponent, MapMarkerInfoWindowDialogInput>(MapMarkerInfoWindowComponent, {
                data: {
                    modal_title: data.modal_title,
                    entities: data.entities
                }
            });
        });
    }

    goList() {
        this.router.navigate(['../'], {relativeTo: this.activatedRoute});
    }

    trash() {
        this.service.getTrashed().subscribe(res => {
            const items = [];
            res.data.forEach(elem => {
                items.push({
                    text: `#${elem.ref_num} - ${elem.name}`,
                    id: elem.id.toString(),
                    date: elem.user_deleted,
                    hasPermanentlyDelete: true,
                    deletedUser: elem.userDeletedBy
                });
            });
            this._showTrashed(items);
        })
    }
    adminTrash() {
        this.service.getAdminTrashed().subscribe(res => {
            const items = [];
            res.data.forEach(elem => {
                items.push({
                    text: `#${elem.ref_num} - ${elem.name}`,
                    id: elem.id.toString(),
                    date: elem.deleted_at,
                    hasPermanentlyDelete: true,
                    deletedUser: elem.deletedBy
                });
            });
            this._showTrashed(items, true);
        })
    }

    // Acs
    delete(id: number) {
        this.store.dispatch(new AssetClassLandDeletedFromTrash({id}))
    }
    restore(id: number, isAdmin = false) {
        if (isAdmin) {

        } else {
            
        }
    }
    private _showTrashed(items, isAdmin = false) {
        let _title = 'Deleted Land Comparables';
        if (isAdmin) {
            _title = 'Admin Deleted Land Comparables';
        }
        this.layoutService.trashedEntities(_title, items, this, isAdmin);
    }

    duplicate(id: number) {
        this.store.dispatch(new AssetClassLandDuplicate({id}))
    }

    private _getLandLocation(el: any): google.maps.LatLng {
      if (el.locationData.latitude !== null && el.locationData.longitude !== null) {
        return new google.maps.LatLng(Number(el.locationData.latitude), Number(el.locationData.longitude))
      }
      const bounds = new google.maps.LatLngBounds();
      const points = el.points.map(p => ({lat: Number(p.lat), lng: Number(p.lng)}));
      const filtered = points.filter(p => p.lat != null && p.lng != null);
      filtered.forEach(p => bounds.extend(p))
      return bounds.getCenter()
    }
}
